import type {
  TLAnalyticsCalculation,
  TLAnalyticsFilter,
  TLDataHarmonizationV2Priority,
  TLPivotAggregate,
  TLSavedFilterResponseConfig,
  TLSavedFilterTargetType,
} from '@/api/gen';
import type { ChartConfig, ColumnFormat } from '@/modus/charts/types';
import type {
  TLAICreateDataCheckType,
  TLAIDataCheckExecutionStatus,
  TLAIDHDataCheckExecution,
  TLAIDHEnrichedDataCheckRecord,
} from '@/types/api';
import { TLAIFilterOperator, TLAISortDirection } from '@/types/api';

export type { ColumnFormat };

//#region ------------ Field Types ------------

export enum TLFieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  TIME = 'time',
}

export type SAPFieldMetadata = {
  INTTYPE: string;
  DATATYPE: string;
  TABNAME: string;
  DECIMALS: number;
  ROLLNAME: string;
  FIELDNAME: string;
  FIELD_TYPE: string;
  DESCRIPTION: string;
  FIELD_LENGTH: number;
};

//#endregion

//#region ------------ Filters and Sorts ------------

export type TableFilter = {
  id: string;
  key: string;
  operator: TLAIFilterOperator;
  value: unknown;
};

export type TableSort = {
  id: string;
  key: string;
  direction: TLAISortDirection;
};

export type SortDirectionOption = {
  value: TLAISortDirection;
  label: string;
};

export const SORT_DIRECTIONS: SortDirectionOption[] = [
  { value: TLAISortDirection.ASC, label: 'Ascending' },
  { value: TLAISortDirection.DESC, label: 'Descending' },
];

export type FilterOperatorOption = {
  value: TLAIFilterOperator;
  label: string;
  badge?: string;
  requiresValue: boolean;
  placeholder?: string;
};

export const FILTER_OPERATORS_MAP: Record<string, FilterOperatorOption> = {
  [TLAIFilterOperator.EQUALS]: { value: TLAIFilterOperator.EQUALS, label: 'Equals', requiresValue: true },
  [TLAIFilterOperator.NOT_EQUALS]: { value: TLAIFilterOperator.NOT_EQUALS, label: 'Not equals', requiresValue: true },
  [TLAIFilterOperator.GREATER_THAN]: {
    value: TLAIFilterOperator.GREATER_THAN,
    label: 'Greater than',
    requiresValue: true,
  },
  [TLAIFilterOperator.LESS_THAN]: { value: TLAIFilterOperator.LESS_THAN, label: 'Less than', requiresValue: true },
  [TLAIFilterOperator.IS_NULL]: { value: TLAIFilterOperator.IS_NULL, label: 'Is empty', requiresValue: false },
  [TLAIFilterOperator.IS_NOT_NULL]: {
    value: TLAIFilterOperator.IS_NOT_NULL,
    label: 'Is not empty',
    requiresValue: false,
  },
  [TLAIFilterOperator.ILIKE]: {
    value: TLAIFilterOperator.ILIKE,
    label: 'Contains',
    badge: 'text',
    requiresValue: true,
  },
  // IN/NOT IN are produced by the persisted-filter dropdowns (multi-select values),
  // not by the filter popover; listed here so their chips render a readable label.
  [TLAIFilterOperator.IN]: {
    value: TLAIFilterOperator.IN,
    label: 'Is any of',
    badge: 'list',
    requiresValue: true,
    placeholder: 'Comma-separated values...',
  },
  [TLAIFilterOperator.NOT_IN]: {
    value: TLAIFilterOperator.NOT_IN,
    label: 'Is none of',
    badge: 'list',
    requiresValue: true,
    placeholder: 'Comma-separated values...',
  },
  // TODO: add "Start with" ?
  [TLAIFilterOperator.CONTAINS_ANY]: {
    value: TLAIFilterOperator.CONTAINS_ANY,
    label: 'Contains any',
    badge: 'list',
    requiresValue: true,
    placeholder: 'Comma-separated values...',
  },
  [TLAIFilterOperator.NOT_CONTAINS_ANY]: {
    value: TLAIFilterOperator.NOT_CONTAINS_ANY,
    label: 'Not contains any',
    badge: 'list',
    requiresValue: true,
    placeholder: 'Comma-separated values...',
  },
  [TLAIFilterOperator.CONTAINS_ALL]: {
    value: TLAIFilterOperator.CONTAINS_ALL,
    label: 'Contains all',
    badge: 'list',
    requiresValue: true,
    placeholder: 'Comma-separated values...',
  },
  [TLAIFilterOperator.NOT_CONTAINS_ALL]: {
    value: TLAIFilterOperator.NOT_CONTAINS_ALL,
    label: 'Not contains all',
    badge: 'list',
    requiresValue: true,
    placeholder: 'Comma-separated values...',
  },
};

export type SavedFilter = {
  filter_id?: string;
  filter_name: string;
  is_public: boolean;
  description?: string | null;
  target_id: string;
  target_type: TLSavedFilterTargetType;
  config: TLSavedFilterResponseConfig;
  //   status: TLSavedFilterStatus;
  created_by?: string;
  //   created_date: string;
  //   updated_by: string;
  //   updated_date: string;
};

//#endregion

//#region ------------ Dashboard and Data checks UI ------------

export type CreateDataCheckFormData = {
  dataChecks: TLAICreateDataCheckType[];
};

export enum TLAIDataHarmonizationLandingTabType {
  DASHBOARDS = 'dashboards',
  LIBRARIES = 'libraries',
  UNIVERSE = 'universe',
  ADMIN_CONSOLE = 'admin-console',
}

export enum TLAIDataHarmonizationDashboardTabType {
  PROGRESS_OVERVIEW = 'progress-overview',
  ERROR_TRACKING = 'error-tracking',
  DATA_CHECKS = 'data-checks',
  DASHBOARD_VIEWS = 'dashboard-views',
}

export enum TLAIDataHarmonizationDataCheckTabType {
  ERROR_DETAIL = 'error-detail',
  EXECUTION_HISTORY = 'execution-history',
}

export type DataCheckRecordWithExecution = TLAIDHEnrichedDataCheckRecord & {
  execution?: TLAIDHDataCheckExecution;
  executionStatus?: TLAIDataCheckExecutionStatus;
};

export enum DataCheckFormStep {
  SELECT_DATA_CHECKS = 'select_data_checks',
  CONFIGURE_DATA_CHECKS = 'configure_data_checks',
}

export type DataCheckConfigFormDataItem = {
  data_check_id: string;
  nickname?: string;
  priority?: TLDataHarmonizationV2Priority;
};

export type DataCheckConfigFormData = {
  dataChecks: DataCheckConfigFormDataItem[];
};

export type CreateDashboardFormData = {
  name: string;
  workstream?: string;
  description: string;
};

export type ActionMenu = {
  label: string;
  callback: (id: string | any) => void;
  Icon?: any;
  variant?: 'default' | 'destructive' | undefined;
  menus?: ActionMenu[];
};

//#endregion

//#region ------------ Dashboard Card/Vignette ------------

export enum DashboardVignetteType {
  LAST_EXECUTION = 'last-execution',
  LAST_ERROR_COUNT = 'last-error-count',
  LAST_SCANNED_COUNT = 'last-scanned-count',
  DATA_CHECKS_COUNT = 'data-checks-count',
  WORKSTREAM = 'workstream',
  TARGET_SCORE = 'target-score',
  DQ_SCORE = 'dq-score',
  CUSTOM_QUERY = 'custom-query',
}

export enum DashboardVignetteIcon {
  // Let's keep the list small in case we change icon library later
  BOX = 'box',
  BOXES = 'boxes',
  BRIEFCASE = 'briefcase',
  FLAG = 'flag',
  GEM = 'gem',
  GLOBE = 'globe',
  LIGHTBULB = 'lightbulb',
  ROCKET = 'rocket',
  SNOWFLAKE = 'snowflake',
}

export type DashboardVignette = {
  type: DashboardVignetteType;
  label?: string;
  format?: ColumnFormat;
  icon?: DashboardVignetteIcon;
  query?: TLAIDHAnalyticsQueryConfig;
};

//#endregion

//#region ------------ Graph Configuration ------------

export enum GraphType {
  BAR = 'bar',
  PIE = 'pie',
  LINE = 'line',
  AREA = 'area',
  METRIC_CARD = 'metric',
  TABLE = 'table',
  TABLE_PIVOT = 'table_pivot',
  GAUGE = 'gauge',
  TABLE_HIERARCHY = 'table_hierarchy',
}

//#region --- Global Filters ---
export enum GlobalFilterType {
  SELECT = 'select',
  DATE = 'date',
  // TIME = 'time',
  // DATETIME = 'date-time',
  // INPUT = 'input',
  // BOOLEAN = 'boolean',
}

// TODO: export type FilterListOfValues = { label: string; value: string }[] | string[];
export type FilterListOfValues = string[];

export type GlobalFilter = {
  key: string;
  type: GlobalFilterType;
  operator: 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte' | 'in' | 'not_in' | 'like' | 'is_null' | 'is_not_null';
  label: string;
  labelPlural: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  range?: 'past' | 'future';
  query?: TLAIDHAnalyticsQueryConfig;
  list?: FilterListOfValues;
  // default?: any;
};
//#endregion

//#region --- Query ---
export type TLAIDHAnalyticsSelectField =
  | string
  | {
      field: string;
      aggregate?: 'sum' | 'avg' | 'count' | 'min' | 'max';
      alias?: string;
      null_values?: string | number;
    };

export type TLAnalyticsPivot = {
  pivot_column: string;
  value_column: string;
  aggregate?: TLPivotAggregate;
  include_totals?: boolean;
};

export type TLAIDHAnalyticsQueryConfig = {
  source_id?: string;
  select: TLAIDHAnalyticsSelectField[];
  calculations?: TLAnalyticsCalculation[];
  filters?: TLAnalyticsFilter[];
  group_by?: string[];
  order_by?: string[];
  distinct?: boolean;
  limit?: number;
  pivot?: TLAnalyticsPivot;
  partition_by_time_grain?: string;
  latest_per_data_check?: boolean;
};
//#endregion

//#region --- Data Transform ---
export type TLAIDHFieldMapping = {
  from: string;
  to: string;
  default_value?: string | number;
};

export type TLAIDHDateFormatConfig = {
  field: string;
  format: 'short' | 'medium' | 'long';
  timezone?: string;
  locale?: string;
};

export type TLAIDHDataFilterConfig = {
  field: string;
  operator: 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte' | 'not_empty' | 'in';
  value?: number | string | string[];
};

export type TLAIDHSortConfig = {
  field: string;
  direction: 'asc' | 'desc';
};

export type TLAIDHMerger = {
  new_key: string;
  keys: string[];
  separator?: string;
};

export type TLAIDHTopNConfig = {
  count: number;
  by: string;
  label_key: string;
  others_label?: string;
};

export type DataTransformConfig = {
  field_mappings?: TLAIDHFieldMapping[];
  calculated_fields?: string[];
  merger?: TLAIDHMerger;
  date_formats?: TLAIDHDateFormatConfig[];
  filters?: TLAIDHDataFilterConfig[];
  /** Applied after `filters` and before `sort`. */
  top_n?: TLAIDHTopNConfig;
  closest_date_key?: string;
  all_zeros_key?: string;
  sort?: TLAIDHSortConfig; // TODO: make it an array ?
  limit?: number;
};
//#endregion

//#region --- Metrics ---
export enum TLAIDHMetricCalculation {
  DIRECT = 'direct',
  QUALITY_SCORE = 'quality_score',
  EXTERNAL_PROP = 'external_prop',
}

/** Which direction of change is good, so a delta can be colored. */
export type TLAIDHMetricSentiment = 'higher_is_better' | 'lower_is_better' | 'neutral';

export type TLAIDHMetricComparisonConfig = {
  /** `previous` compares the last two points; `first` compares the last point against the start of the window. */
  mode: 'previous' | 'first';
  /** Names the period being compared against, e.g. `'vs previous day'`. */
  label?: string;
};

export type TLAIDHMetricConfig = {
  id: string;
  label: string;
  calculation: TLAIDHMetricCalculation;
  format: 'number' | 'percentage' | 'currency';
  /** Compact headline notation (`$460.4M` / `45.3K`); pair with a money metric. */
  compact?: boolean;
  data_key?: string;
  decimal_places?: number;
  description?: string;
  show_condition?: string;
  /** How the window reduces to the headline number. Defaults to `latest`. */
  value_scope?: 'latest' | 'window_total';
  /**
   * Row field the metric's window is ordered by, oldest last. Required for `value_scope` and
   * `comparison` to read the right end of the window.
   */
  series_date_key?: string;
  /** Adds a delta against an earlier point. Needs `series_date_key` */
  comparison?: TLAIDHMetricComparisonConfig;
  /** Defaults to `neutral`, which renders the delta in muted ink rather than green/red. */
  sentiment?: TLAIDHMetricSentiment;
};
//#endregion

//#region --- Settings : Metric ---
export type MetricSettings = {
  direction?: 'row' | 'column';
  wrap?: boolean;
};
//#endregion

export type TLAIDHLayoutConfig = {
  col_span?: number; // 1 to 12
  height?: number;
};

type SharedGraphConfigProps = {
  id: string;
  title: string;
  graph_type: GraphType;
  query: TLAIDHAnalyticsQueryConfig;
  global_filters?: '*' | string[];
  global_filters_exclude?: string[];
  filter_key_map?: Record<string, string>;
  data_transform?: DataTransformConfig;
  layout?: TLAIDHLayoutConfig;
  description?: string;
};

export type ChartGraphConfig = SharedGraphConfigProps & ChartConfig;

export type MetricsGraphConfig = SharedGraphConfigProps & {
  metrics?: TLAIDHMetricConfig[];
  metrics_settings?: MetricSettings;
};

export type GraphConfiguration = ChartGraphConfig | MetricsGraphConfig;

export type TLAIDHProgressOverviewConfig = {
  global_filters: GlobalFilter[];
  graphs: GraphConfiguration[];
};

//#endregion
